/**
 * Floating debug-copy icon button, visible only on host hover/focus-within
 * so it never competes with primary UI affordances. No app-level $notify
 * import — success/failure feedback lives entirely in the icon's textContent,
 * keeping this dev-only tool dependency-free from the app's notify stack.
 *
 * DEV-only, enforced rather than documented — same idiom as
 * `overflow-within.ts`'s structural guard and `state/active-host.ts`'s
 * `_devSlot`. The affordance is a real `<button>` appended to the consumer's
 * own subtree, so an ungated build puts a focusable, screen-reader-announced
 * "Copy debug bundle" control on every surface the directive touches and
 * hands an end user a debug bundle; `opacity: 0` hides it from sight only.
 */

import { noChange } from 'lit'
import { AsyncDirective, directive } from 'lit/async-directive.js'
import type { ElementPart } from 'lit'

interface DebugState {
	button: HTMLElement
	icon: HTMLElement
	onHostEnter: () => void
	onHostLeave: () => void
	onHostFocusIn: () => void
	onHostFocusOut: () => void
	onClick: (e: MouseEvent) => void
	resetTimer: ReturnType<typeof setTimeout> | null
}

const stateMap = new WeakMap<HTMLElement, DebugState>()

class DebugDirective extends AsyncDirective {
	private _element: HTMLElement | null = null

	render(_payload: () => unknown) {
		return noChange
	}

	override update(part: ElementPart, [payload]: [() => unknown]) {
		// Gate before anything is created, so a production build carries no
		// listener, no state entry, and — after dead-code elimination — none of
		// the markup or strings below.
		if (!import.meta.env.DEV) return noChange

		const element = part.element as HTMLElement
		if (stateMap.has(element)) return noChange

		this._element = element

		// Anchor the absolute button to the host. Deferred to a microtask because
		// ElementPart update runs during template instantiation, while the element
		// is still in a DocumentFragment — getComputedStyle there returns '' (not
		// 'static'), which silently skipped the fix-up and let the button anchor
		// to the viewport instead of the host.
		queueMicrotask(() => {
			if (getComputedStyle(element).position === 'static') {
				element.style.position = 'relative'
			}
		})

		const button = document.createElement('button')
		button.setAttribute('aria-label', 'Copy debug bundle')
		button.setAttribute('tabindex', '0')
		button.style.cssText = `
			display: flex;
			align-items: center;
			justify-content: center;
			position: absolute;
			top: 6px;
			right: 6px;
			z-index: 20;
			width: 28px;
			height: 28px;
			border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
			border: none;
			background: color-mix(in srgb, var(--schmancy-sys-color-surface-containerHigh, #1e1e1e) 85%, transparent);
			color: var(--schmancy-sys-color-surface-on, #e0e0e0);
			cursor: pointer;
			opacity: 0;
			transition: opacity 150ms ease, transform 120ms ease;
			pointer-events: none;
		`

		const icon = document.createElement('span')
		icon.textContent = '🐛'
		icon.style.cssText = `font-size: 13px; line-height: 1; display: block;`
		button.appendChild(icon)

		element.appendChild(button)

		const show = () => {
			button.style.opacity = '1'
			button.style.pointerEvents = 'auto'
		}
		const hide = () => {
			button.style.opacity = '0'
			button.style.pointerEvents = 'none'
		}

		// Track whether focus is within the host so blur from the button itself
		// doesn't immediately hide it before the click fires.
		let focusedWithin = false
		let hoveredHost = false

		const onHostEnter = () => {
			hoveredHost = true
			show()
		}
		const onHostLeave = () => {
			hoveredHost = false
			if (!focusedWithin) hide()
		}
		const onHostFocusIn = () => {
			focusedWithin = true
			show()
		}
		const onHostFocusOut = () => {
			// Defer so the button click fires before we hide.
			setTimeout(() => {
				focusedWithin = false
				if (!hoveredHost) hide()
			}, 50)
		}

		const onClick = (e: MouseEvent) => {
			e.stopPropagation()
			e.preventDefault()

			const s = stateMap.get(element)
			if (!s) return
			if (s.resetTimer !== null) clearTimeout(s.resetTimer)

			let resolved: unknown
			try {
				resolved = payload()
			} catch {
				resolved = null
			}

			const text = typeof resolved === 'string' ? resolved : JSON.stringify(resolved, null, 2)

			navigator.clipboard.writeText(text).then(
				() => {
					icon.textContent = '✅'
					s.resetTimer = setTimeout(() => {
						icon.textContent = '🐛'
						s.resetTimer = null
					}, 1200)
				},
				() => {
					icon.textContent = '❌'
					s.resetTimer = setTimeout(() => {
						icon.textContent = '🐛'
						s.resetTimer = null
					}, 1200)
				},
			)
		}

		element.addEventListener('mouseenter', onHostEnter)
		element.addEventListener('mouseleave', onHostLeave)
		element.addEventListener('focusin', onHostFocusIn)
		element.addEventListener('focusout', onHostFocusOut)
		button.addEventListener('click', onClick)

		stateMap.set(element, {
			button,
			icon,
			onHostEnter,
			onHostLeave,
			onHostFocusIn,
			onHostFocusOut,
			onClick,
			resetTimer: null,
		})

		return noChange
	}

	override disconnected(): void {
		if (!this._element) return
		const element = this._element
		const s = stateMap.get(element)
		if (!s) return
		if (s.resetTimer !== null) clearTimeout(s.resetTimer)
		element.removeEventListener('mouseenter', s.onHostEnter)
		element.removeEventListener('mouseleave', s.onHostLeave)
		element.removeEventListener('focusin', s.onHostFocusIn)
		element.removeEventListener('focusout', s.onHostFocusOut)
		s.button.removeEventListener('click', s.onClick)
		if (element.contains(s.button)) element.removeChild(s.button)
		stateMap.delete(element)
		this._element = null
	}
}

export const debug = directive(DebugDirective)
